import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import { Link as RouterLink, useParams, useSearchParams } from 'react-router-dom';
import StatusChip from '../../components/StatusChip';
import { useAllTenantFleetsQuery, useRunComparisonQuery } from '../../services/routing/queries';
import { NO_FLEET_ID, namedFleet, namesById, plannedDay } from '../../utils/format';
import type { RunComparisonRouteSide, RunComparisonSide } from '../../types/routing';

/** `rides_assigned` is what the API calls it and what a reader wants to see said. */
const measure = (key: string) => key.replace(/_/g, ' ');

const round = (value: number) => Math.round(value * 100) / 100;

/**
 * The direction of the change, which is the column an operator actually reads. Deltas are shown
 * with their sign and no judgement: whether more pools or fewer deadhead miles is an improvement
 * depends on what was being tried.
 */
const delta = (base: number, candidate: number) => {
  const difference = round(candidate - base);
  if (difference === 0) return '—';
  return difference > 0 ? `+${difference}` : String(difference);
};

const rideList = (rides: number[]) => (rides.length ? rides.join(', ') : '—');

const Side = ({ label, side }: { label: string; side: RunComparisonSide }) => (
  <Paper sx={{ p: 2 }}>
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Link component={RouterLink} to={`/runs/${side.run_id}`} underline="hover">
        <Typography variant="h6">Run #{side.run_id}</Typography>
      </Link>
      <StatusChip status={side.status} />
      <Chip label={side.trigger} size="small" variant="outlined" />
    </Stack>
    <Typography variant="body2" color="text.secondary">
      {plannedDay(side.from_datetime)} (NY) · tenant {side.tenant ?? 'default'} · engine{' '}
      {side.engine ?? '—'}
      {side.rerun_of ? ` · re-plans #${side.rerun_of}` : ''}
    </Typography>
    <Typography variant="body2" color="text.secondary">
      world: {side.world_rides ?? '—'} rides, {side.world_routes ?? '—'} routes
      {side.world_pinned_from ? ` as run #${side.world_pinned_from} saw them` : ''} ·{' '}
      {side.worker_concurrency ?? '—'} workers
    </Typography>
  </Paper>
);

const RouteSideCells = ({ side }: { side: RunComparisonRouteSide | null }) => {
  if (!side) {
    // A route the other run planned and this one never saw — blank, not zero: it planned
    // nothing here because it was never given the route.
    return (
      <>
        <TableCell colSpan={3}>
          <Typography variant="body2" color="text.secondary">
            not planned
          </Typography>
        </TableCell>
      </>
    );
  }
  return (
    <>
      <TableCell>
        <StatusChip status={side.outcome} />
      </TableCell>
      <TableCell align="right">{side.rides_assigned}</TableCell>
      <TableCell align="right">{side.pools_assigned}</TableCell>
    </>
  );
};

/**
 * What one run planned differently from another. A rerun arrives here with nothing to pick: it
 * records the run it re-planned, so the comparison it wants is the one it opens with.
 */
export const RunComparePage = () => {
  const { runId = '' } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const to = searchParams.get('to');
  const [against, setAgainst] = useState(to ?? '');

  const { data, isLoading, error } = useRunComparisonQuery(runId, to);
  // A bare id is not what an operator reads, and a route in no fleet is the Myle fleet rather
  // than a blank — the same reading as the run's own routes listing. Both sides are looked up,
  // because fleet names belong to a tenant: a cross-tenant comparison naming a base route from
  // the candidate's fleets would put another tenant's name on it.
  const candidateFleets = useAllTenantFleetsQuery(data?.candidate.tenant ?? '').data;
  const baseFleets = useAllTenantFleetsQuery(data?.base.tenant ?? '').data;
  const candidateFleetNames = namesById(candidateFleets ?? []);
  const baseFleetNames = namesById(baseFleets ?? []);

  // A run planned by one worker is deterministic; past that, the route-level diff carries the
  // race as well as the configuration, and the page has to say so. A run that never recorded its
  // worker count cannot be read as the deterministic case — it is the unknown one, and the
  // caveat is the honest answer to not knowing.
  const workers = [data?.base.worker_concurrency, data?.candidate.worker_concurrency];
  const workersUnknown = data != null && workers.some(count => count == null);
  const raced = workersUnknown || workers.some(count => (count ?? 0) > 1);

  const onCompare = () => {
    const trimmed = against.trim();
    setSearchParams(trimmed ? { to: trimmed } : {});
  };

  return (
    <Stack spacing={2} useFlexGap>
      <Link component={RouterLink} to={`/runs/${runId}`} underline="hover">
        ← Run #{runId}
      </Link>

      <Stack direction="row" spacing={2} alignItems="flex-start" flexWrap="wrap" useFlexGap>
        <Typography variant="h6">What run #{runId} planned differently</Typography>
        <Stack
          direction="row"
          spacing={1}
          alignItems="flex-start"
          sx={{ ml: { xs: 0, sm: 'auto' }, width: { xs: '100%', sm: 'auto' } }}
        >
          <TextField
            label="Compare against run"
            size="small"
            value={against}
            onChange={event => setAgainst(event.target.value)}
            inputProps={{ 'aria-label': 'compare against run' }}
            helperText="blank compares a rerun with the run it re-planned"
            sx={{ width: 220, flexGrow: { xs: 1, sm: 0 } }}
          />
          <Button variant="outlined" onClick={onCompare} sx={{ mt: 0.25 }}>
            Compare
          </Button>
        </Stack>
      </Stack>

      {isLoading && <CircularProgress aria-label="loading comparison" />}
      {error && <Alert severity="error">{(error as Error).message}</Alert>}

      {data && (
        <>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Side label="BASE — judged against" side={data.base} />
            </Grid>
            <Grid item xs={12} md={6}>
              <Side label="CANDIDATE — the plan being judged" side={data.candidate} />
            </Grid>
          </Grid>

          {data.comparable.notes.length > 0 ? (
            <Alert severity="warning">
              <AlertTitle>These runs are not a clean comparison</AlertTitle>
              <ul style={{ margin: 0, paddingLeft: '1.2em' }}>
                {data.comparable.notes.map(note => (
                  <li key={note}>{note}</li>
                ))}
              </ul>
            </Alert>
          ) : (
            <Alert severity="success">
              Same day, tenant, route set and world, and both runs succeeded — what follows is the
              configuration&apos;s doing.
            </Alert>
          )}

          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              CONFIGURATION CHANGED
            </Typography>
            {data.config_changed.length === 0 ? (
              <Typography color="text.secondary">
                Both runs planned with the same configuration — any difference below is the world
                or the run itself, not a setting.
              </Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Key</TableCell>
                      <TableCell>Base</TableCell>
                      <TableCell>Candidate</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.config_changed.map(change => (
                      <TableRow key={change.key}>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                          {change.key}
                        </TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                          {change.base ?? '—'}
                        </TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                          {change.candidate ?? '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>

          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              TOTALS
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Measure</TableCell>
                    <TableCell align="right">Base</TableCell>
                    <TableCell align="right">Candidate</TableCell>
                    <TableCell align="right">Change</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(data.totals).map(([key, total]) => (
                    <TableRow key={key}>
                      <TableCell>{measure(key)}</TableCell>
                      <TableCell align="right">{round(total.base)}</TableCell>
                      <TableCell align="right">{round(total.candidate)}</TableCell>
                      <TableCell align="right">{delta(total.base, total.candidate)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          <Paper sx={{ p: 2 }}>
            <Stack direction="row" spacing={2} alignItems="baseline" sx={{ mb: 1 }} flexWrap="wrap">
              <Typography variant="subtitle2" color="text.secondary">
                ROUTES THAT CAME OUT DIFFERENTLY
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {data.routes.length} of {data.routes.length + data.routes_identical} ·{' '}
                {data.routes_identical} identical
              </Typography>
            </Stack>
            {raced && (
              // Seen for real: the same day re-planned with a rider cap that changed nothing
              // still moved rides between routes, because the workers claim them in whatever
              // order they get there. The totals hold; a single route's diff does not.
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {workersUnknown
                  ? 'One of these runs did not record how many workers planned it'
                  : `Planned by more than one worker (${data.base.worker_concurrency} and ${data.candidate.worker_concurrency})`}
                , and past one worker the routes race each other for rides — so which route a ride
                landed on can differ between two runs of the same day whatever the configuration
                did. Read the totals for the effect of a setting, and a single route&apos;s rides
                as one way it could have been dealt.
              </Typography>
            )}
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Route</TableCell>
                    <TableCell>Fleet</TableCell>
                    <TableCell>Base outcome</TableCell>
                    <TableCell align="right">Rides</TableCell>
                    <TableCell align="right">Pools</TableCell>
                    <TableCell>Candidate outcome</TableCell>
                    <TableCell align="right">Rides</TableCell>
                    <TableCell align="right">Pools</TableCell>
                    <TableCell>Rides gained</TableCell>
                    <TableCell>Rides lost</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.routes.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={10}>
                        <Typography color="text.secondary">
                          Every route planned the same rides in the same order.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {data.routes.map(route => (
                    <TableRow key={route.route_id} hover>
                      <TableCell>
                        {/* The run that actually planned it: a route the candidate never saw has
                            its detail on the base run, not a page that does not exist. */}
                        <Link
                          component={RouterLink}
                          to={`/runs/${
                            route.candidate ? data.candidate.run_id : data.base.run_id
                          }/routes/${route.route_id}`}
                          underline="hover"
                        >
                          {route.route_id}
                        </Link>
                      </TableCell>
                      <TableCell>
                        {namedFleet(
                          route.fleet_id ?? NO_FLEET_ID,
                          route.candidate ? candidateFleetNames : baseFleetNames
                        )}
                      </TableCell>
                      <RouteSideCells side={route.base} />
                      <RouteSideCells side={route.candidate} />
                      <TableCell>{rideList(route.rides_added)}</TableCell>
                      <TableCell>{rideList(route.rides_removed)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}
    </Stack>
  );
};

export default RunComparePage;
